import type { DynamicProps, PMCInsiteClientSignUpService } from '@capsule/insite-panel/types.js'
import PmcBridge from '@capsule/pmc-bridge'

// SVG files must be imported without dynamic import to be detected by Webpack
// The main entry point is dynamically imported by the router, so the SVGs should not be in it.
// The router is imported normally.
import '@capsule/svgs-library/capsule-google.svg'
import '@capsule/svgs-library/capsule-apple.svg'
import '@capsule/svgs-library/capsule-prismashop.svg'
import '@capsule/svgs-library/capsule-mail.svg'
import '@capsule/svgs-library/capsule-arrow-left.svg'
import '@capsule/svgs-library/capsule-not-valid.svg'
import '@capsule/svgs-library/capsule-valid.svg'
import '@capsule/svgs-library/capsule-show.svg'
import '@capsule/svgs-library/capsule-check.svg'

export default class Router {
	defaultSignUpService: PMCInsiteClientSignUpService
	brandLegalLink: string
	customRoutes: any
	isOpenGranted: (hash: string) => boolean

	routes: string[]
	container: HTMLElement
	appElement: HTMLElement
	closeButton: HTMLElement
	pmcBridge: PmcBridge
	isLoaded: boolean

	constructor({
		defaultSignUpService,
		brandLegalLink,
		customRoutes = [],
		isOpenGranted = () => true
	}: {
		defaultSignUpService: PMCInsiteClientSignUpService
		brandLegalLink: string
		customRoutes?: any
		isOpenGranted?: (hash: string) => boolean
	}) {
		this.defaultSignUpService = defaultSignUpService
		this.brandLegalLink = brandLegalLink
		this.customRoutes = customRoutes
		this.isOpenGranted = isOpenGranted

		this.routes = [
			...[
				'/compte',
				'/connexion',
				'/lien-connexion',
				'/inscription',
				'/reinitialisation',
				'/finalisation'
			],
			...this.customRoutes.map((route: any) => route.path)
		]
		this.container = document.querySelector('.userLoginTunnel') as HTMLElement
		this.appElement = document.querySelector('.userLoginTunnel-app') as HTMLElement
		this.closeButton = this.container.querySelector('.userLoginTunnel-close') as HTMLElement
		this.isLoaded = false

		this.pmcBridge = new PmcBridge()

		this.onHashChange = this.onHashChange.bind(this)
		this.onUserPressEscape = this.onUserPressEscape.bind(this)
	}

	/**
	 * Initialize the module
	 */
	init() {
		this.addEvents()
		this.checkOpening() && this.openPanel()
	}

	/**
	 * Checks if the panel can open
	 * @returns {boolean} Open is granted
	 */
	checkOpening() {
		const hash = window.location.hash.substring(1)
		return this.routes.includes(hash) && this.isOpenGranted(hash)
	}

	/**
	 * Add events
	 */
	addEvents() {
		window.addEventListener('hashchange', this.onHashChange)
		document.addEventListener('insitePanel', (e: any) => {
			const dynamicProps = e.detail as DynamicProps
			this.appElement.dataset.dynamicProps = JSON.stringify(dynamicProps)
			window.location.hash = dynamicProps.route
		})
	}

	/**
	 * On hash change event
	 * @param {Event} e Event data
	 */
	onHashChange(e: HashChangeEvent) {
		const previousHash = e?.oldURL ? e.oldURL.split('#')[1] : ''
		const currentHash = window.location.hash.substring(1)

		if (this.checkOpening() && !this.appElement.hasChildNodes()) {
			this.openPanel()
		} else if (!this.routes.includes(currentHash) && this.routes.includes(previousHash)) {
			this.closePanel()
		}
	}

	/**
	 * Action on user key press
	 * @param e Event listener object
	 */
	onUserPressEscape(e: KeyboardEvent) {
		if (e.key === 'Escape' && this.container.classList.contains('show')) {
			this.closeButton.dispatchEvent(
				new window.Event('click', {
					bubbles: true
				})
			)
		}
	}

	/**
	 * Open panel
	 */
	async openPanel() {
		;(document.querySelector('html') as HTMLElement).classList.add('userLoginTunnel-active')
		this.container.classList.add('show')

		window.addEventListener('keydown', this.onUserPressEscape)

		if (!this.isLoaded && !this.appElement.hasChildNodes()) {
			this.isLoaded = true

			const { default: InsitePanel } = await import(
				/* webpackChunkName: "dynamic-import-insitepanel" */ '@capsule/insite-panel'
			)
			const insitePanel = new InsitePanel({
				defaultSignUpService: this.defaultSignUpService,
				brandLegalLink: this.brandLegalLink,
				customRoutes: this.customRoutes
			})
			insitePanel.init()
		}
	}

	/**
	 * Close panel
	 */
	closePanel() {
		;(document.querySelector('html') as HTMLElement).classList.remove('userLoginTunnel-active')
		this.container.classList.remove('show')

		window.removeEventListener('keydown', this.onUserPressEscape)
		delete this.container.dataset.ult
	}
}
